import { useCallback, useEffect, useRef, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import type { Map as MapboxMap } from 'mapbox-gl';

export interface ViewportFetchArgs {
  map: MapboxMap;
  /** [west, south, east, north] for the current viewport. */
  bounds: [number, number, number, number];
  zoom: number;
  /** Aborted as soon as a newer fetch starts; pass it to fetch() to cancel the request. */
  signal: AbortSignal;
}

export interface ViewportGeoJSONOptions {
  /** Below this the layer renders nothing and no fetch is made. Default: none. */
  minZoom?: number;
  /** How long to wait after the map stops moving. Default 600ms. */
  debounceMs?: number;
}

/**
 * Keeps a GeoJSON layer in step with the map viewport: fetch on mount, refetch
 * (debounced) whenever the map stops moving, and drop everything on unmount or
 * when the layer is hidden.
 *
 * The three field-ops overlays (census demographics, OSM roads, election
 * precincts) each hand-rolled this same skeleton, and only one of them got the
 * race right. Results from a superseded fetch are ignored here, so a slow early
 * pan can never overwrite a faster later one, whether or not the fetcher honours
 * the abort signal.
 *
 * `fetcher` must be stable (wrap it in useCallback) — it is an effect dependency,
 * so a new identity every render would refetch on every render.
 */
export function useViewportGeoJSON<T extends GeoJSON.GeoJsonObject>(
  visible: boolean,
  fetcher: (args: ViewportFetchArgs) => Promise<T | null>,
  { minZoom, debounceMs = 600 }: ViewportGeoJSONOptions = {},
): { data: T | null; loading: boolean } {
  const { current: mapRef } = useMap();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef<AbortController | null>(null);

  const run = useCallback(async () => {
    const map = mapRef?.getMap() as MapboxMap | undefined;
    if (!map || !visible) return;

    const zoom = map.getZoom();
    if (minZoom !== undefined && zoom < minZoom) {
      inFlight.current?.abort();
      setData(null);
      setLoading(false);
      return;
    }

    const b = map.getBounds();
    if (!b) return;

    // Supersede any fetch still running: its result is stale by definition.
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;

    setLoading(true);
    try {
      const result = await fetcher({
        map,
        bounds: [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()],
        zoom,
        signal: controller.signal,
      });
      if (!controller.signal.aborted) setData(result);
    } catch (err) {
      // A cancelled request is expected on a fast pan, not a failure.
      if (!controller.signal.aborted) {
        console.warn('Viewport layer fetch failed:', err);
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [mapRef, visible, fetcher, minZoom]);

  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map || !visible) {
      setLoading(false);
      return;
    }

    const onMoveEnd = () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(run, debounceMs);
    };

    map.on('moveend', onMoveEnd);
    run();

    return () => {
      map.off('moveend', onMoveEnd);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      inFlight.current?.abort();
      setLoading(false);
    };
  }, [mapRef, visible, run, debounceMs]);

  return { data: visible ? data : null, loading };
}
